import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { downloadBlob, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";

const RANGE_PRESETS = [
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 3 months", days: 90 },
  { key: "183", label: "Last 6 months", days: 183 },
  { key: "365", label: "Last 12 months", days: 365 },
  { key: "custom", label: "Custom date range", days: null },
];

function iso(d) {
  return d.toISOString().slice(0, 10);
}

function computeRange(presetKey, customFrom, customTo) {
  if (presetKey === "custom") {
    return { date_from: customFrom, date_to: customTo };
  }
  const preset = RANGE_PRESETS.find((p) => p.key === presetKey);
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - (preset?.days || 30));
  return { date_from: iso(start), date_to: iso(today) };
}

export default function DailyNotesExportDialog({ open, onOpenChange, clientId, clientName }) {
  const [presetKey, setPresetKey] = useState("183");
  const [format, setFormat] = useState("pdf");
  const [customFrom, setCustomFrom] = useState(iso(new Date(Date.now() - 30 * 86400 * 1000)));
  const [customTo, setCustomTo] = useState(iso(new Date()));
  const [busy, setBusy] = useState(false);

  const isCustom = presetKey === "custom";
  const range = useMemo(
    () => computeRange(presetKey, customFrom, customTo),
    [presetKey, customFrom, customTo],
  );

  const rangeInvalid =
    isCustom && (!range.date_from || !range.date_to || range.date_from > range.date_to);

  const submit = async () => {
    if (busy) return;
    if (rangeInvalid) {
      toast.error("Please choose a valid date range");
      return;
    }
    setBusy(true);
    const qs = new URLSearchParams({
      date_from: range.date_from,
      date_to: range.date_to,
    }).toString();
    const path = `/clients/${clientId}/daily-logs/export.${format}?${qs}`;
    const safeName = (clientName || "service-user").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
    const filename = `daily-notes-${safeName}-${range.date_from}-to-${range.date_to}.${format}`;
    try {
      await downloadBlob(path, filename);
      toast.success(`${format.toUpperCase()} downloaded`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent
        className="max-w-md w-[92vw] sm:w-full"
        data-testid="daily-notes-export-dialog"
      >
        <DialogHeader>
          <DialogTitle>Download Daily Notes</DialogTitle>
          <DialogDescription>
            Choose a date range and format. Exports include only records you have access to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
              Date range
            </Label>
            <RadioGroup
              value={presetKey}
              onValueChange={setPresetKey}
              className="mt-2 grid grid-cols-1 gap-2"
              data-testid="daily-notes-export-range"
            >
              {RANGE_PRESETS.map((p) => (
                <div key={p.key} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={p.key}
                    id={`range-${p.key}`}
                    data-testid={`range-${p.key}`}
                  />
                  <Label htmlFor={`range-${p.key}`} className="text-sm cursor-pointer">
                    {p.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {isCustom && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="date-from" className="text-[10px] uppercase tracking-widest text-slate-500">
                    From
                  </Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={customFrom}
                    max={customTo}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    data-testid="daily-notes-export-date-from"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="date-to" className="text-[10px] uppercase tracking-widest text-slate-500">
                    To
                  </Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={customTo}
                    min={customFrom}
                    onChange={(e) => setCustomTo(e.target.value)}
                    data-testid="daily-notes-export-date-to"
                    className="mt-1 h-11"
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
              Format
            </Label>
            <RadioGroup
              value={format}
              onValueChange={setFormat}
              className="mt-2 grid grid-cols-2 gap-2"
              data-testid="daily-notes-export-format"
            >
              <div className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
                <RadioGroupItem value="pdf" id="fmt-pdf" data-testid="format-pdf" />
                <Label htmlFor="fmt-pdf" className="text-sm cursor-pointer">
                  PDF report
                </Label>
              </div>
              <div className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
                <RadioGroupItem value="csv" id="fmt-csv" data-testid="format-csv" />
                <Label htmlFor="fmt-csv" className="text-sm cursor-pointer">
                  CSV data
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="h-11 lg:h-9"
            data-testid="daily-notes-export-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || rangeInvalid}
            className="h-11 lg:h-9 bg-sky-700 hover:bg-sky-800"
            data-testid="daily-notes-export-submit"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Preparing…
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-1.5" /> Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
